import { List, Map } from "immutable";

import { calculateCappedSessionDuration } from "@seneca/session-aggregation";
import { AnyModuleStatsI } from "@seneca/stats-service-types";

import {
  composeSelector,
  composeSelectorCreator
} from "seneca-common/utils/selectors/compose-selectors";

import { PATH_TO_SESSION_MODULES_STATE } from "../../../consts";
import * as modulesSelectors from "./modules";
import { SessionModules } from "./modules";
import * as moduleSelectors from "./modules/module";

export { moduleSelectors, modulesSelectors };

type State = Map<string, Map<string, SessionModules>>;

export function getSessionModules(
  state: State,
  sessionId: string
): SessionModules {
  return state.get(PATH_TO_SESSION_MODULES_STATE)!.get(sessionId)!;
}

export const getSessionModule = composeSelector(
  getSessionModules,
  modulesSelectors.getSessionModule,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getContentModuleId = composeSelector(
  getSessionModules,
  modulesSelectors.getContentModuleId,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getContentId = composeSelector(
  getSessionModules,
  modulesSelectors.getContentId,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getWordId = composeSelector(
  getSessionModules,
  modulesSelectors.getWordId,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getSkill = composeSelector(
  getSessionModules,
  modulesSelectors.getSkill,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getTextToSpeechDisabled = composeSelector(
  getSessionModules,
  modulesSelectors.getTextToSpeechDisabled,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getModuleType = composeSelector(
  getSessionModules,
  modulesSelectors.getModuleType,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getContent = composeSelector(
  getSessionModules,
  modulesSelectors.getContent,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getTestingActive = composeSelector(
  getSessionModules,
  modulesSelectors.getTestingActive,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getTimeStarted = composeSelector(
  getSessionModules,
  modulesSelectors.getTimeStarted,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getTimeFinished = composeSelector(
  getSessionModules,
  modulesSelectors.getTimeFinished,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getModuleState = composeSelector(
  getSessionModules,
  modulesSelectors.getModuleState,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getCompleted = composeSelector(
  getSessionModules,
  modulesSelectors.getCompleted,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getSubmitted = composeSelector(
  getSessionModules,
  modulesSelectors.getSubmitted,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getGaveUp = composeSelector(
  getSessionModules,
  modulesSelectors.getGaveUp,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getUserAnswer = composeSelector(
  getSessionModules,
  modulesSelectors.getUserAnswer,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const getScore = composeSelector(
  getSessionModules,
  modulesSelectors.getScore,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const wasCorrect = composeSelector(
  getSessionModules,
  modulesSelectors.wasCorrect,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const wasIncorrect = composeSelector(
  getSessionModules,
  modulesSelectors.wasIncorrect,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const moduleStillInProgress = composeSelector(
  getSessionModules,
  modulesSelectors.moduleStillInProgress,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const moduleContainsGIF = composeSelector(
  getSessionModules,
  modulesSelectors.moduleContainsGIF,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export const createScoreSelector = composeSelectorCreator(
  getSessionModules,
  modulesSelectors.createScoreSelector,
  (_sessionId: string, sessionModuleId: string) => [sessionModuleId]
);

export function getNumberOfSessionModules(
  state: State,
  sessionId: string
): number {
  const sessionModules = getSessionModules(state, sessionId);
  return sessionModules ? sessionModules.size : 0;
}

export function sessionModulesExist(state: State, sessionId: string): boolean {
  const sessionModules = getSessionModules(state, sessionId);
  return !!sessionModules;
}

export function getNumberOfCorrectSessionModules(
  state: State,
  sessionId: string
): number {
  const sessionModules = getSessionModules(state, sessionId);

  return sessionModules
    ? sessionModules.filter(moduleSelectors.wasCorrect).size
    : 0;
}

export function getNumberOfCompletedSessionModules(
  state: State,
  sessionId: string
): number {
  const sessionModules = getSessionModules(state, sessionId);

  return sessionModules
    ? sessionModules.filter(moduleSelectors.getCompleted).size
    : 0;
}

export const getSessionContentModuleIds = (
  state: any,
  sessionId: string
): List<string> => {
  const sessionModules = getSessionModules(state, sessionId);
  return sessionModules!
    .valueSeq()
    .toList()
    .map(moduleSelectors.getContentModuleId);
};

export const getNumberDistinctModulesWithGif = (
  state: State,
  sessionId: string
): number => {
  const sessionModules = getSessionModules(state, sessionId);
  if (!sessionModules) return 0;
  const modulesWithGIF = sessionModules
    .valueSeq()
    .toList()
    .reduce((modulesWithGIF: any, module: any) => {
      if (moduleSelectors.moduleContainsGIF(module)) {
        modulesWithGIF.add(moduleSelectors.getContentModuleId(module));
      }

      return modulesWithGIF;
    }, new Set());
  return modulesWithGIF.size;
};

export const getCappedSessionDuration = (
  state: State,
  sessionId: string
): number => {
  const sessionModulesMap = getSessionModules(state, sessionId);
  if (!sessionModulesMap) return 0;

  const sessionModules = sessionModulesMap
    .valueSeq()
    .map(m => ({
      timeStarted: moduleSelectors.getTimeStarted(m),
      timeFinished:
        moduleSelectors.getTimeFinished(m) ?? new Date().toISOString()
    }))
    .toArray();

  return calculateCappedSessionDuration(sessionModules as AnyModuleStatsI[]);
};
